// src/containers/FormBuilderDashboard/hooks/usePresets.js
import _ from 'lodash';
import { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addWorkItemPreset, updateWorkItemPreset } from '../../../../actions/apiWorkItemPresets';

const usePresets = () => {
  const dispatch = useDispatch();
  const work_item_presets = useSelector(state => state.settings_refresh?.work_item_presets || []);
  const all_tabs = useSelector(state => state.settings_site?.all_tabs || []);
  const related_installations = useSelector(state => state.settings_dealerships?.related_installations || []);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(['all']);
  const [selectedDealerships, setSelectedDealerships] = useState(['all']);
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [presetForm, setPresetForm] = useState({});

  const allSectionOptions = useMemo(() => {
    const options = new Set();
    all_tabs.forEach(tab => {
      if (tab.form && tab.sections) {
        tab.sections.split(',').map(s => s.trim()).filter(Boolean).forEach(section => {
          options.add(`${tab.form}_${section}`);
        });
      }
    });
    return Array.from(options).sort();
  }, [all_tabs]);

  const normalizedPresets = useMemo(() => {
    return work_item_presets.map(preset => ({
      ...preset,
      _normalizedCategory: (preset.category || 'other').toLowerCase().trim(),
      _sectionsArray: preset.section ? preset.section.split(',').map(s => s.trim()).filter(Boolean) : [],
      _dealershipsArray: preset.dealership ? preset.dealership.split(',').map(s => s.trim()).filter(Boolean) : [],
    }));
  }, [work_item_presets]);

  const filteredPresets = useMemo(() => {
    const searchLower = searchTerm.toLowerCase().trim();
    return normalizedPresets.filter(preset => {
      const matchesSearch = preset.name.toLowerCase().includes(searchLower);
      const matchesCategory = selectedCategories.includes('all') || selectedCategories.includes(preset._normalizedCategory);
      const matchesDealership =
        selectedDealerships.includes('all') ||
        preset._dealershipsArray.length === 0 ||
        selectedDealerships.some(dealership => preset._dealershipsArray.includes(dealership));
      return matchesSearch && matchesCategory && matchesDealership;
    });
  }, [normalizedPresets, searchTerm, selectedCategories, selectedDealerships]);

  const openPresetModal = (preset = null) => {
    setEditingPreset(preset);

    const defaultForm = {
      name: '',
      description: '',
      category: 'cosmetic',
      layout_type: 'default',
      active: true,
      dealership: '',

      // Approval & Status - using recommended instead of recommend
      pass: false,
      pass_editable: true,
      pass_locked: false,
      pass_inputtype: 'checkbox',
      pass_options: '',
      pass_allowed_profiles: '',

      required: '',
      required_editable: true,
      required_locked: false,
      required_inputtype: 'checkbox',
      required_options: '',
      required_allowed_profiles: '',

      recommended: false,
      recommended_editable: true,
      recommended_locked: false,
      recommended_inputtype: 'checkbox',
      recommended_options: '',
      recommended_allowed_profiles: '',

      requested: false,
      requested_editable: true,
      requested_locked: false,
      requested_inputtype: 'checkbox',
      requested_options: '',
      requested_allowed_profiles: '',

      approved: false,
      approved_editable: true,
      approved_locked: false,
      approved_inputtype: 'checkbox',
      approved_options: '',
      approved_allowed_profiles: '',

      declined: false,
      declined_editable: true,
      declined_locked: false,
      declined_inputtype: 'checkbox',
      declined_options: '',
      declined_allowed_profiles: '',

      completed: false,
      completed_editable: true,
      completed_locked: false,
      completed_inputtype: 'checkbox',
      completed_options: '',
      completed_allowed_profiles: '',

      // Pricing
      parts_price: 0,
      parts_price_editable: true,
      parts_price_locked: false,
      parts_price_inputtype: 'text',
      parts_price_options: '',
      parts_price_allowed_profiles: '',

      labor_price: 0,
      labor_price_editable: true,
      labor_price_locked: false,
      labor_price_inputtype: 'text',
      labor_price_options: '',
      labor_price_allowed_profiles: '',

      labor_units: 0,
      labor_units_editable: true,
      labor_units_locked: false,
      labor_units_inputtype: 'text',
      labor_units_options: '',
      labor_units_allowed_profiles: '',

      quantity: 1,
      quantity_editable: true,
      quantity_locked: false,
      quantity_inputtype: 'text',
      quantity_options: '',
      quantity_allowed_profiles: '',

      estimate_total: 0,
      estimate_total_editable: true,
      estimate_total_locked: false,
      estimate_total_inputtype: 'text',
      estimate_total_options: '',
      estimate_total_allowed_profiles: '',

      // Custom headers
      custom_header_1: '',
      custom_header_1_editable: true,
      custom_header_1_locked: false,
      custom_header_1_inputtype: 'text',
      custom_header_1_options: '',
      custom_header_1_allowed_profiles: '',

      custom_header_2: '',
      custom_header_2_editable: true,
      custom_header_2_locked: false,
      custom_header_2_inputtype: 'text',
      custom_header_2_options: '',
      custom_header_2_allowed_profiles: '',

      custom_header_3: '',
      custom_header_3_editable: true,
      custom_header_3_locked: false,
      custom_header_3_inputtype: 'text',
      custom_header_3_options: '',
      custom_header_3_allowed_profiles: '',

      custom_header_4: '',
      custom_header_4_editable: true,
      custom_header_4_locked: false,
      custom_header_4_inputtype: 'text',
      custom_header_4_options: '',
      custom_header_4_allowed_profiles: '',

      custom_header_5: '',
      custom_header_5_editable: true,
      custom_header_5_locked: false,
      custom_header_5_inputtype: 'text',
      custom_header_5_options: '',
      custom_header_5_allowed_profiles: '',

      custom_header_6: '',
      custom_header_6_editable: true,
      custom_header_6_locked: false,
      custom_header_6_inputtype: 'text',
      custom_header_6_options: '',
      custom_header_6_allowed_profiles: '',

      exclude_from_work_items: false,
    };

    setPresetForm(preset ? { ...defaultForm, ...preset } : defaultForm);
    setPresetModalOpen(true);
  };

  const closePresetModal = () => {
    setPresetModalOpen(false);
    setEditingPreset(null);
    setPresetForm({});
  };

  const handlePresetSave = async () => {
    console.log('=== SAVING PRESET ===');
    console.log('Full presetForm being saved:', presetForm);

    const payload = { ...presetForm };

    // Remove any remaining fields that don't exist in the table
    delete payload.recommend;           // remove old recommend key
    delete payload.recommend_by_uid;
    delete payload.recommend_by_date;

    delete payload._normalizedCategory;
    delete payload._sectionsArray;
    delete payload._dealershipsArray;

    console.log('Final cleaned payload sent to backend:', payload);

    try {
      const action = editingPreset 
        ? updateWorkItemPreset(editingPreset.id, payload) 
        : addWorkItemPreset(payload);

      await dispatch(action);
      console.log('Save successful');
      closePresetModal();
    } catch (err) {
      console.error('Failed to save preset:', err);
    }
  };

  return {
    filteredPresets,
    searchTerm,
    setSearchTerm,
    selectedCategories,
    setSelectedCategories,
    selectedDealerships,
    setSelectedDealerships,
    presetModalOpen,
    setPresetModalOpen,
    editingPreset,
    setEditingPreset,
    presetForm,
    setPresetForm,
    openPresetModal,
    closePresetModal,
    handlePresetSave,
  };
};

export default usePresets;